import { eraForYear, type Era } from "@bd/content";
import { dpow } from "./dmath";

export const START_YEAR = 1965;
export const WEEKS_PER_YEAR = 52;
export const WEEKS_PER_QUARTER = 13;
export const SEASON_QUARTERS = 280;
export const SEASON_WEEKS = SEASON_QUARTERS * WEEKS_PER_QUARTER; // 3640 weeks: Q1 1965 .. Q4 2034
export const FINALE_YEAR = START_YEAR + SEASON_QUARTERS / 4; // 2035
/** Solo "normal" speed: about 120 in-game weeks per real hour (280 quarters ≈ 30 hours). */
export const WEEKS_PER_PLAY_HOUR = 120;

export const SEASON_NAMES = ["Winter", "Spring", "Summer", "Autumn"] as const;
export type SeasonName = (typeof SEASON_NAMES)[number];

export interface GameDate {
  week: number; // absolute week index from 0
  year: number;
  weekOfYear: number; // 1..52
  quarter: number; // 1..4
  quarterIndex: number; // 0..279
  season: SeasonName;
  era: Era;
  label: string; // "Spring 1984"
}

export function dateOf(week: number): GameDate {
  const year = START_YEAR + Math.floor(week / WEEKS_PER_YEAR);
  const weekOfYear = (week % WEEKS_PER_YEAR) + 1;
  const quarter = Math.floor((weekOfYear - 1) / WEEKS_PER_QUARTER) + 1;
  const season = SEASON_NAMES[quarter - 1];
  return {
    week, year, weekOfYear, quarter, quarterIndex: Math.floor(week / WEEKS_PER_QUARTER), season, era: eraForYear(year),
    label: `${season} ${year}`,
  };
}

export function yearOf(week: number): number {
  return START_YEAR + Math.floor(week / WEEKS_PER_YEAR);
}

/** Fractional year, used to interpolate audience sizes smoothly. */
export function yearFrac(week: number): number {
  return START_YEAR + week / WEEKS_PER_YEAR;
}

export function weekOf(year: number, weekOfYear = 1): number {
  return (year - START_YEAR) * WEEKS_PER_YEAR + (weekOfYear - 1);
}

export function isQuarterStart(week: number): boolean {
  return week % WEEKS_PER_QUARTER === 0;
}

export function isYearStart(week: number): boolean {
  return week % WEEKS_PER_YEAR === 0;
}

/** In-world money rises with each era: 2.8% a year (1965 = 1.0, 2000 ≈ 2.6, 2034 ≈ 6.7). */
export function priceIndex(week: number): number {
  return dpow(1.028, week / WEEKS_PER_YEAR);
}

export function playHours(week: number): number {
  return week / WEEKS_PER_PLAY_HOUR;
}

/** The holiday shelf: weeks 44–50, when games are bought as presents. */
export function isHolidayShelf(week: number): boolean {
  const w = (week % WEEKS_PER_YEAR) + 1;
  return w >= 44 && w <= 50;
}

/** Weeks until the next holiday shelf opens (0 while it's open). */
export function weeksToHoliday(week: number): number {
  const w = (week % WEEKS_PER_YEAR) + 1;
  if (w >= 44 && w <= 50) return 0;
  return w < 44 ? 44 - w : 52 - w + 44;
}
